import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterComponentFactory, IFilterProvider } from '../ifilter-provider';
import { FilterProvidersToken } from '../filter-provider-token';
import { combineLatest, ReplaySubject, tap } from "rxjs";
import { FilterMetadata } from '@irian-ro/abstract-resource';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-column-filters',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './column-filters.component.html',
  styleUrl: './column-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnFiltersComponent implements OnInit {
  @Input({ required: true }) type!: string;

  private filterFactory: FilterComponentFactory<any> | undefined;

  @Input({ required: true })
  set filterMatchMode(value: string) {
    this.filterMatchMode$.next(value);
  }

  filterMatchMode$ = new ReplaySubject<string>();

  @Input()
  set props(value: { [key: string]: any }) {
    this.props$.next(value);
  }

  props$ = new ReplaySubject<{ [key: string]: any }>();

  private destroyRef = inject(DestroyRef); // Inject DestroyRef

  config$ = combineLatest([this.filterMatchMode$, this.props$]).pipe(
    takeUntilDestroyed(this.destroyRef),
    tap(([filterMatchMode, props]) => this.doRender(filterMatchMode, props))
  );

  private instance: Component | null | undefined = null;

  @Output() filtersChange = new EventEmitter<FilterMetadata>();

  constructor(
    @Inject(FilterProvidersToken) private filters: IFilterProvider<any>[],
    private readonly container: ViewContainerRef
  ) {
  }

  ngOnInit(): void {
    this.filterFactory = this.filters.find((filter) => filter.type === this.type)?.factory;
    this.config$.subscribe();
  }

  doRender(filterMatchMode: string, props: { [key: string]: any }): void {
    if (this.filterFactory) {
      const options = {
        filterMatchMode: filterMatchMode,
        filterChangeEvent: this.filtersChange,
        props: props,
      };

      if (this.instance) {
        this.filterFactory.bindProps(this.instance, options);
      } else {
       this.instance = this.filterFactory.createInstance(this.container);
        this.filterFactory.bindProps(this.instance, options);
        this.filterFactory.subscribeToChanges(this.instance, options);
      }
    }
  }
}
